export const APP_ROUTE_BASE_PATHS = {
  ol: "/ol",
  qb: "/qb",
  db: "/db",
  def: "/def",
  dl: "/dl",
  dc: "/dc",
  lb: "/lb",
  rb: "/rb",
  s: "/s",
  st: "/st",
  te: "/te",
  wr: "/wr",
}

export const APP_KEY_BASE_PATHS = {
  ol: "OL",
  qb: "QB",
  db: "DefensiveBack",
  def: "DefLineMan",
  dl: "DL",
  dc: "DefensiveCoordinator",
  lb: "LineBacker",
  rb: "RunningBack",
  s: "Safety",
  st: "ST",
  te: "TightEnd",
  wr: "WideReceiver",
}


export enum AppId {
  Dashboard = "dashboard",
  OL = "ol",
  QB = "qb",
  DB = "db",
  DEF = "def",
  DL = "dl",
  DC = "dc",
  LB = "lb",
  RB = "rb",
  S = "s",
  ST = "st",
  TE = "te",
  WR = "wr",
}

export enum Subscriptions {
  Team = "team",
  OL = "ol",
  QB = "qb",
  DB = "db",
  DEF = "def",
  DL = "dl",
  DC = "dc",
  LB = "lb",
  RB = "rb",
  S = "s",
  ST = "st",
  TE = "te",
  WR = "wr",
}

/**
 * AppSessionEnum code per app — the key the backend's session manager (and
 * each user's product_device_limit) uses. Must match the SessionProvider
 * appKey each app registers with.
 */
export const APP_ID_BY_SESSIONS: Record<Exclude<AppId, AppId.Dashboard>, string> = {
  [AppId.OL]: "OL",
  [AppId.QB]: "QB",
  [AppId.DB]: "DB",
  [AppId.DEF]: "DEF",
  [AppId.DL]: "DL",
  [AppId.DC]: "DC",
  [AppId.LB]: "LB",
  [AppId.RB]: "RB",
  [AppId.S]: "S",
  [AppId.ST]: "ST",
  [AppId.TE]: "TE",
  [AppId.WR]: "WR",
}

export type AppDetails = {
  id: AppId
  label: string
  code: string
  category: string
  description: string
  isOfflineCapable?: boolean
}

export type App = AppId | string

export const APP_DETAILS: Record<
  Exclude<AppId, AppId.Dashboard>,
  AppDetails
> = {
  [AppId.OL]: {
    id: AppId.OL,
    label: "O-Line",
    code: "OL",
    category: "Offense",
    description: "Practice entries, grading, and install tracking for OL.",
    isOfflineCapable: true,
  },
  [AppId.QB]: {
    id: AppId.QB,
    label: "Quarterback",
    code: "QB",
    category: "Offense",
    description: "Scripts, throws, and situational installs for quarterbacks.",
    isOfflineCapable: true,
  },
  [AppId.DB]: {
    id: AppId.DB,
    label: "Defensive Back",
    code: "DB",
    category: "Defense",
    description: "Practice entries and grading for defensive backs.",
    isOfflineCapable: true,
  },
  [AppId.DEF]: {
    id: AppId.DEF,
    label: "Practice",
    code: "DEF",
    category: "Practice",
    description: "Defensive practice planning and analysis.",
    isOfflineCapable: true,
  },
  [AppId.DL]: {
    id: AppId.DL,
    label: "D-Line",
    code: "DL",
    category: "Defense",
    description: "Practice entries and grading for defensive line.",
    isOfflineCapable: true,
  },
  [AppId.DC]: {
    id: AppId.DC,
    label: "D-Coordinator",
    code: "DC",
    category: "Defense",
    description: "Practice entries and grading for defensive coordinators.",
    isOfflineCapable: true,
  },
  [AppId.LB]: {
    id: AppId.LB,
    label: "Linebacker",
    code: "LB",
    category: "Defense",
    description: "Practice entries and grading for linebackers.",
    isOfflineCapable: true,
  },
  [AppId.RB]: {
    id: AppId.RB,
    label: "Running Back",
    code: "RB",
    category: "Offense",
    description: "Practice entries and grading for running backs.",
    isOfflineCapable: true,
  },
  [AppId.S]: {
    id: AppId.S,
    label: "Safety",
    code: "S",
    category: "Defense",
    description: "Practice entries and grading for safeties.",
    isOfflineCapable: true,
  },
  [AppId.ST]: {
    id: AppId.ST,
    label: "Special Teams",
    code: "ST",
    category: "Special Teams",
    description: "Special teams practice and game planning.",
    isOfflineCapable: true,
  },
  [AppId.TE]: {
    id: AppId.TE,
    label: "Tight End",
    code: "TE",
    category: "Offense",
    description: "Practice entries and grading for tight ends.",
    isOfflineCapable: true,
  },
  [AppId.WR]: {
    id: AppId.WR,
    label: "Wide Receiver",
    code: "WR",
    category: "Offense",
    description: "Practice entries and grading for wide receivers.",
    isOfflineCapable: true,
  },
}

export const APP_TO_SUBSCRIPTIONS_MAPPING: Record<
  Exclude<AppId, AppId.Dashboard>,
  Subscriptions[]
> = {
  // OL app validates: 'mofo-ol', 'ol', 'team'
  [AppId.OL]: [Subscriptions.OL, Subscriptions.Team],
  
  // QB app validates: 'qb', 'qbraid'
  [AppId.QB]: [Subscriptions.QB, Subscriptions.Team],
  
  // DB app validates: 'db', 'defensiveback'
  [AppId.DB]: [Subscriptions.DB, Subscriptions.Team],
  
  // DEF app validates: 'def', 'defense', 'defpractice'
  [AppId.DEF]: [Subscriptions.DEF, Subscriptions.Team],
  
  // DL app validates: 'dl', 'deflineman'
  [AppId.DL]: [Subscriptions.DL, Subscriptions.Team],

  // DC app validates: 'dc', 'defensivecoordinator'
  [AppId.DC]: [Subscriptions.DC, Subscriptions.Team],
  
  // LB app validates: 'lb', 'linebacker'
  [AppId.LB]: [Subscriptions.LB, Subscriptions.Team],
  
  // RB app validates: 'rb', 'runningback'
  [AppId.RB]: [Subscriptions.RB, Subscriptions.Team],
  
  // S app validates: 's', 'safetyposition'
  [AppId.S]: [Subscriptions.S, Subscriptions.Team],
  
  // ST app validates: 'st', 'specialteams'
  [AppId.ST]: [Subscriptions.ST, Subscriptions.Team],
  
  // TE app validates: 'te', 'tightend'
  [AppId.TE]: [Subscriptions.TE, Subscriptions.Team],
  
  // WR app validates: 'wr', 'widereceiver'
  [AppId.WR]: [Subscriptions.WR, Subscriptions.Team],
}

/**
 * Maps all possible subscription type names (including aliases/variants) to their normalized Subscriptions enum
 * Each app may use different naming conventions from the API, so we map them all to the canonical enum values
 */
export const SUBSCRIPTION_NAME_MAP: Record<string, Subscriptions> = {
  // OL variants
  'mofo-ol': Subscriptions.OL,
  'ol': Subscriptions.OL,
  
  // QB variants
  'qb': Subscriptions.QB,
  'qbraid': Subscriptions.QB,
  
  // DB variants
  'db': Subscriptions.DB,
  'defensiveback': Subscriptions.DB,
  
  // DEF variants
  'def': Subscriptions.DEF,
  'defense': Subscriptions.DEF,
  'defpractice': Subscriptions.DEF,
  
  // DL variants
  'dl': Subscriptions.DL,
  'deflineman': Subscriptions.DL,

  // DC variants
  'dc': Subscriptions.DC,
  'defensivecoordinator': Subscriptions.DC,
  
  // LB variants
  'lb': Subscriptions.LB,
  'linebacker': Subscriptions.LB,
  
  // RB variants
  'rb': Subscriptions.RB,
  'runningback': Subscriptions.RB,
  
  // S variants
  's': Subscriptions.S,
  'safetyposition': Subscriptions.S,
  
  // ST variants
  'st': Subscriptions.ST,
  'specialteams': Subscriptions.ST,
  
  // TE variants
  'te': Subscriptions.TE,
  'tightend': Subscriptions.TE,
  
  // WR variants
  'wr': Subscriptions.WR,
  'widereceiver': Subscriptions.WR,
  
  // Team
  'team': Subscriptions.Team,

  // "[Practice] suite" Stripe product — one subscription that unlocks the
  // entire suite. Normalized to Team, which every app's mapping accepts.
  'practicesuite': Subscriptions.Team,
}
